import type {
    DuplicateClusterMember,
} from "@mrflark/spl_sdk";
import {
    Button,
    Modal,
} from "@mrflark/ui";
import {
    Crown,
    Eye,
    Heart,
    Tag,
    Trash2,
} from "lucide-preact";
import {
    useEffect,
    useState,
} from "preact/compat";

const formatBytes = (bytes?: number): string => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso?: string): string => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

type MergeDuplicatesModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (keepId: string, deleteIds: string[]) => void;
    members: DuplicateClusterMember[];
    isSaving?: boolean;
};

// pick-the-canonical modal. operator chooses which row to keep; the
// rest get hard-deleted after metadata merge. shows the ground truth
// for each candidate (upload date, file size, view/vote counts, tag
// count) so the choice is obvious. defaults to the oldest member —
// usually the original upload, with the longest accumulated history
export const MergeDuplicatesModal = ({
    isOpen,
    onClose,
    onConfirm,
    members,
    isSaving = false,
}: MergeDuplicatesModalProps) => {
    const [keepId, setKeepId] = useState<string | null>(null);

    useEffect(() => {
        if (
            members.length > 0 &&
            (!keepId || !members.find((m) => m.id === keepId))
        ) {
            setKeepId(members[0].id);
        }
    }, [members, keepId]);

    if (members.length < 2) return null;

    const deleteIds = members
        .filter((member) => member.id !== keepId)
        .map((member) => member.id);
    const keptMember = members.find((member) => member.id === keepId) ?? null;

    const mergedTagCount = (() => {
        const set = new Set<string>();
        for (const member of members) {
            for (const tag of member.tags) set.add(tag);
        }
        return set.size;
    })();
    const mergedViews = members.reduce((acc, m) => acc + (m.views ?? 0), 0);
    const mergedVotes = members.reduce((acc, m) => acc + (m.votes ?? 0), 0);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Merge duplicates">
            <div className="space-y-4 text-sm">
                <p className="text-text-secondary">
                    Pick the canonical image. Its row keeps the merged metadata
                    (tags unioned, view + vote counts summed). The other{" "}
                    <span className="text-amber-300 font-medium">
                        {deleteIds.length}{" "}
                        {deleteIds.length === 1 ? "image" : "images"}
                    </span>{" "}
                    and their files are permanently deleted.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {members.map((member) => {
                        const isKept = member.id === keepId;
                        return (
                            <button
                                type="button"
                                key={member.id}
                                onClick={() => setKeepId(member.id)}
                                aria-pressed={isKept}
                                className={`group relative text-left rounded-lg border-2 overflow-hidden transition-all duration-150 ${
                                    isKept
                                        ? "border-accent shadow-[0_0_0_4px_rgba(255,255,255,0.05)]"
                                        : "border-border hover:border-accent/50 opacity-70 hover:opacity-100"
                                }`}
                            >
                                {isKept && (
                                    <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-white text-xs font-semibold tracking-wide">
                                        <Crown size={12} /> Keep
                                    </div>
                                )}
                                {!isKept && (
                                    <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-secondary/80 backdrop-blur text-text-muted text-xs">
                                        <Trash2 size={12} /> Delete
                                    </div>
                                )}
                                <div className="aspect-video bg-bg-tertiary relative">
                                    {member.thumbnail_url
                                        ? (
                                            <img
                                                src={member.thumbnail_url}
                                                alt=""
                                                className={`w-full h-full object-cover ${
                                                    isKept ? "" : "grayscale"
                                                }`}
                                            />
                                        )
                                        : null}
                                </div>
                                <div className="p-3 space-y-1.5 bg-bg-secondary">
                                    <div className="text-xs font-mono text-text-muted truncate">
                                        {member.id}
                                    </div>
                                    <div className="text-xs text-text-secondary">
                                        {formatDate(member.upload_date)}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                                        <span>
                                            {formatBytes(
                                                member.file_size_bytes,
                                            )}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <Tag size={10} />
                                            {member.tags.length}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <Eye size={10} />
                                            {member.views}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <Heart size={10} />
                                            {member.votes}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="rounded-md border border-border bg-bg-tertiary/50 p-3 text-xs text-text-secondary space-y-1">
                    <div className="font-medium text-text-primary">
                        After merge
                    </div>
                    <div>
                        Kept row{" "}
                        <span className="font-mono text-text-primary">
                            {keptMember?.id.slice(0, 8) ?? "—"}…
                        </span>{" "}
                        will hold {mergedTagCount}{" "}
                        unique tag{mergedTagCount === 1 ? "" : "s"},{" "}
                        {mergedViews} view{mergedViews === 1 ? "" : "s"},{" "}
                        {mergedVotes} vote{mergedVotes === 1 ? "" : "s"}.
                    </div>
                    <div>
                        {deleteIds.length}{" "}
                        file{deleteIds.length === 1 ? "" : "s"}{" "}
                        will be removed from disk and the database.
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="error"
                        size="sm"
                        onClick={() => keepId && onConfirm(keepId, deleteIds)}
                        disabled={!keepId || isSaving}
                    >
                        <Crown size={14} className="mr-1" />
                        Keep selected, delete {deleteIds.length}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
